import axios from 'axios';

const isDevEnv = process.env.NODE_ENV === 'development';
const log = (...args) => {
  if (isDevEnv) {
    console.log(...args);
  }
};
const warn = (...args) => {
  if (isDevEnv) {
    console.warn(...args);
  }
};

// Determine base URL logic
const envBase = (process.env.REACT_APP_API_URL || '').trim();
const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);

let baseURL;
if (isLocal) {
  baseURL = envBase || 'http://127.0.0.1:8000/api';
} else {
  baseURL = envBase || 'https://api.myfleetpulse.app/api';  // Explicit fallback for prod
}

log('[API] Base URL:', baseURL);

// base axios instance
const api = axios.create({
  baseURL,
});

// Request interceptor: This runs BEFORE every request is sent.
// It reads the token from localStorage and adds it to the headers.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      log('Sending token:', !!token);
    } else {
      warn('No token found in localStorage');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const clearAuthAndRedirect = (reason) => {
  warn(reason);
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
  localStorage.removeItem('userInfo');
  window.location.href = '/';
};

// Refresh tokens rotate and blacklist on the server, so concurrent 401s must
// share ONE refresh call or every request after the first will invalidate the session.
let refreshInFlight = null;
const refreshAccessToken = () => {
  if (!refreshInFlight) {
    const refreshToken = localStorage.getItem('refresh');
    if (!refreshToken) return Promise.reject(new Error('no refresh token'));
    refreshInFlight = axios
      // Served from core/urls.py, not the maintenance app's /auth/ prefix
      .post(`${baseURL}/token/refresh/`, { refresh: refreshToken })
      .then((response) => {
        const newToken = response.data.access;
        localStorage.setItem('token', newToken);
        if (response.data.refresh) {
          localStorage.setItem('refresh', response.data.refresh);
        }
        return newToken;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Response interceptor: This runs AFTER a response is received.
// It checks for 401/403 errors and logs the user out if their token is invalid.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    // Handle 401 - try to refresh token first
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearAuthAndRedirect('Token refresh failed, clearing auth state');
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 - Forbidden (could be expired token or permission issue)
    if (status === 403) {
      // Check if it's an auth issue
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('token') || 
          detail.toLowerCase().includes('credentials') ||
          detail.toLowerCase().includes('authentication')) {
        clearAuthAndRedirect('403 auth error, clearing auth state');
      }
      // Otherwise it's a permission error, let the component handle it
    }

    return Promise.reject(error);
  }
);

export default api;
